import { download, generateCsv, mkConfig } from "export-to-csv"
import { useMoney } from "../../../../../currency/useMoney"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { MonetaryAmount } from "../../../../../currency/currencyTypes"
import { toCurrency, toDinero } from "../../../../../currency/currencyLib"
import { Order, OrderLine } from "../../../../../merchant/controlKitchenTypes"

export function useExportPurchasesToCsv() {
    const moneyFactory = useMoney({ locale: "da-DK" })
    const { l10n } = useAppLocalization()
    const csvOptions = mkConfig({
        fieldSeparator: ";",
        quoteStrings: true,
        decimalSeparator: ".",
        useBom: true,
        useKeysAsHeaders: true,
        filename: `Facilitynet-Purchases`,
    })

    function getCurrencyString(amount: MonetaryAmount | undefined) {
        if (!amount) return ""
        const currency = toCurrency(amount.currency)
        return moneyFactory.getLocalizedCurrencySymbol(currency)
    }

    function getAmountString(amount: MonetaryAmount | undefined) {
        if (!amount) return ""
        const dinero = toDinero(amount)
        return moneyFactory.formatAsNumber(dinero)
    }

    const mapOrderToCsvRow = (order: Order, orderLine: OrderLine) => ({
        "Order Id": order.id,
        "PSP reference": order.paymentDetails?.paymentId ?? "",
        "Transaction time": order.created,
        "Transaction type": order.orderType === "REFUND" ? "refund" : "payment",
        "Order status": order.orderStatus ?? "",
        "Product id": orderLine.productId,
        "Product name": orderLine.name,
        Items: orderLine.items,
        [`Item price (${getCurrencyString(orderLine.itemPrice)})`]: getAmountString(orderLine.itemPrice),
        [`Amount (${getCurrencyString(orderLine.price)})`]: getAmountString(orderLine.price),
        "User id": order.customer?.id ?? "",
        "Employee id": order.customer?.employeeId ?? "",
        Initials: order.customer?.initials ?? "",
        Name: order.customer?.displayName ?? "",
        Email: order.customer?.email ?? "",
        "User group id": order.customer?.userGroup?.id ?? "",
        "User group": order.customer?.userGroup?.name ?? "",
        Company: order.accountingCustomerParty?.name ?? "",
        "Payment method": l10n.getStringForEnum("purchases-payment-method", order.paymentDetails?.method ?? "", ""),
        "Sales location": "",
        Channel: order.salesChannel ?? "",
    })

    const exportPurchasesToCsv = (data: Order[] | null | undefined) => {
        const exportRows =
            data && data.length > 0
                ? data.flatMap((order) =>
                      order.orderLines ? order.orderLines.map((orderLine) => mapOrderToCsvRow(order, orderLine)) : [],
                  )
                : [
                      Object.keys(mapOrderToCsvRow({} as Order, {} as OrderLine)).reduce(
                          (acc, key) => ({ ...acc, [key]: "" }),
                          {},
                      ),
                  ]

        const csv = generateCsv(csvOptions)(exportRows)
        download(csvOptions)(csv)
    }

    return { exportPurchasesToCsv }
}
